/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import HeaderAndSidebar from 'components/templates/HeaderAndSidebar';
import { useState } from 'react';
import {
  convertToOutgoAggregateResultKindDisplayString,
  OutgoAggregateResult,
  OutgoAggregateResultKind,
} from 'types';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

type Props = {
  isLoading: boolean;
  resultKind: OutgoAggregateResultKind;
  outgoAggregateResults: OutgoAggregateResult[];
  onSubmit: (
    kind: string | null,
    bedinDate: Date | null,
    endDate: Date | null,
  ) => void;
};

const OutgoAggregation: React.FC<Props> = ({
  isLoading,
  resultKind,
  outgoAggregateResults,
  onSubmit,
}) => {
  const [kind, setKind] = useState('');
  const handleKindChange = (event: SelectChangeEvent) => {
    setKind(event.target.value);
  };

  const [beginDate, setBeginDate] = useState<Date | null>(null);
  const handleChangeBeginDate = (newDate: Date | null) => {
    setBeginDate(newDate);
  };

  const [endDate, setEndDate] = useState<Date | null>(null);
  const handleChangeEndDate = (newDate: Date | null) => {
    setEndDate(newDate);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const selectedKind = data.get('kind');
    onSubmit(selectedKind?.toString() || null, beginDate, endDate);
  };

  return (
    <HeaderAndSidebar pageTitle="支出集計">
      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            支出集計
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <Stack spacing={3} maxWidth={400}>
              <Select
                label="種別"
                name="kind"
                value={kind}
                onChange={handleKindChange}
                autoWidth
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value={0}>家賃</MenuItem>
                <MenuItem value={1}>食費</MenuItem>
                <MenuItem value={2}>日用品</MenuItem>
                <MenuItem value={3}>趣味</MenuItem>
                <MenuItem value={4}>通信</MenuItem>
                <MenuItem value={5}>交際費</MenuItem>
                <MenuItem value={6}>光熱費</MenuItem>
                <MenuItem value={9001}>その他</MenuItem>
              </Select>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Grid item xs={12} md={12} lg={12}>
                  <DatePicker
                    label="開始日"
                    onChange={handleChangeBeginDate}
                    value={beginDate}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={12}>
                  <DatePicker
                    label="終了日"
                    onChange={handleChangeEndDate}
                    value={endDate}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Grid>
              </LocalizationProvider>
              <Button
                type="submit"
                variant="contained"
                sx={{ maxWidth: '64px' }}
              >
                集計
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            グラフ
          </Typography>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Line
              data={{
                labels: outgoAggregateResults
                  .map((result) => `${result.year}/${result.month}`)
                  .reverse(),
                datasets: [
                  {
                    label:
                      convertToOutgoAggregateResultKindDisplayString(
                        resultKind,
                      ),
                    data: outgoAggregateResults
                      .map((result) => result.amount)
                      .reverse(),
                    fill: false,
                    borderColor: 'rgb(255, 99, 132)',
                  },
                ],
              }}
            />
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            結果
          </Typography>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Table stickyHeader sx={{ maxHeight: 440 }}>
              <TableHead>
                <TableRow>
                  <TableCell>月</TableCell>
                  <TableCell>金額</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outgoAggregateResults.map((result, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={index}>
                    <TableCell>{`${result.year}/${result.month}`}</TableCell>
                    <TableCell>
                      {result.amount.toLocaleString('ja-JP')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Grid>
    </HeaderAndSidebar>
  );
};

export default OutgoAggregation;
